/* ============================================================================
   FitLibrary — fitlibrary-product-reviews.js
   PRODUCT REVIEW VIEW CONTROLLER. Phase 2A.

   Turns the ten product review sections into views over the single review
   source. Before this file each section carried its own six reviews and its
   own typed-in aggregate as schema settings; they were the data. They are now
   markup only, and every figure and every card on the page comes from
   window.FitLibraryReviews.

   THIS FILE IS DATA PLUMBING. It renders the SAME components in the SAME
   order into the SAME containers as the Liquid it replaces:

     · the proof row  — .nr-stars, .nr__score, .nr__readers, in that order,
                        inside .nr__proof, with .nr__count added between the
                        score and the readers line
     · the cards      — .nr-card > .nr-card__name, .nr-card__title,
                        .nr-card__stars, .nr-card__body, tinted
                        ivory / sage / blush / mist in that repeating cycle

   The card DOM is built element by element with textContent rather than by
   pasting HTML, so it is byte-for-byte the DOM that snippets/
   fitlibrary-nr-card.liquid produced, and no review text can ever be
   interpreted as markup.

   TWO QUESTIONS, TWO ANSWERS, ONE DATASET.
     FitLibraryReviews.productSummary(handle)
         every APPROVED review for this exact product -> average and count.
         A 2-star approved review is counted here.
     FitLibraryReviews.productReviews(handle, { minRating: 4 })
         approved AND at or above the highlight threshold -> the carousel.
         That same 2-star review is not shown here.
   The threshold decides what is SHOWN. It never decides what is COUNTED.
   This file must never compute an average from the list it just rendered.

   READER COUNT IS NOT A REVIEW COUNT. The readers line is a business claim
   about audience size and is rendered as its own element, from its own
   setting, next to but never merged with "Based on N reviews".

   NO PERSISTENCE, NO SECRETS. This is a read-only view. It stores nothing —
   no localStorage, no sessionStorage, no cookie — and holds no token or
   endpoint of any kind.

   FAILURE MODE IS HONEST. If the source is unavailable the proof row and the
   track stay empty. They do not fall back to a number typed into a setting,
   because that number is exactly what this migration removed.
   ============================================================================ */

(function (window, document) {
  "use strict";

  if (window.__flProductReviewsBound) return;
  window.__flProductReviewsBound = true;

  /* The tint cycle the ten sections shipped: ivory, sage, blush, mist,
     ivory, sage. Kept as a repeating cycle so a section showing more or
     fewer than six cards still alternates the same way. */
  var TINTS = ["ivory", "sage", "blush", "mist"];

  var EVENT = "fitlibrary:reviews-updated";

  function source() {
    return window.FitLibraryReviews || null;
  }

  function fmt(template, token, value) {
    return String(template || "").split(token).join(value);
  }

  /* ------------------------------------------------------------- proof row

     Rebuilt in full each time so a re-render cannot leave a stale figure
     behind. The readers line travels as a data attribute on the section
     rather than as markup, so this function owns the whole row and the row
     always ends up in one known order. */

  function renderProof(root, R, handle) {
    var proof = root.querySelector("[data-nr-proof]");
    if (!proof) return;

    var sum = R.productSummary(handle);
    var readers = root.getAttribute("data-nr-readers") || "";
    var scoreFormat = root.getAttribute("data-nr-score-format") || "{avg} out of 5";
    var countMany = root.getAttribute("data-nr-count-format") || "Based on {n} reviews";
    var countOne = root.getAttribute("data-nr-count-one") || "Based on 1 review";

    while (proof.firstChild) proof.removeChild(proof.firstChild);

    var scoreText = sum.count ? fmt(scoreFormat, "{avg}", sum.averageDisplay) : "";

    /* Stars. Same snippet output as before, now fractional over a real
       average: 4.64 draws four full stars and a fifth filled to 64%. */
    var stars = document.createElement("span");
    stars.innerHTML = R.starsHTML(
      sum.average,
      sum.count ? "Average rating " + scoreText : "Not yet rated"
    );
    if (stars.firstChild) proof.appendChild(stars.firstChild);

    if (scoreText) {
      var score = document.createElement("span");
      score.className = "nr__score";
      score.textContent = scoreText;
      proof.appendChild(score);
    }

    /* The real, dynamic review count. This is the number of approved reviews
       for this product — not the number of cards in the carousel, and not the
       readers line. */
    if (sum.count) {
      var count = document.createElement("span");
      count.className = "nr__count";
      count.textContent = sum.count === 1 ? countOne : fmt(countMany, "{n}", String(sum.count));
      proof.appendChild(count);
    }

    if (readers) {
      var rd = document.createElement("span");
      rd.className = "nr__readers";
      rd.textContent = readers;
      proof.appendChild(rd);
    }
  }

  /* ----------------------------------------------------------------- cards

     One card, built to match snippets/fitlibrary-nr-card.liquid exactly:
     the same element names, the same classes, the same order, and the same
     rule that a card with neither a title nor a body is not rendered. */

  function buildCard(R, review, tint) {
    if (!review.title && !review.body) return null;

    var art = document.createElement("article");
    art.className = "nr-card nr-card--" + tint;

    /* Same element, same class, same position as before: the age is part of
       this one line, never a second row, a label or a badge. Composed by the
       shared source so the Landing carousel and these ten carousels cannot
       disagree about the format. */
    var who = R.reviewerLine ? R.reviewerLine(review) : review.reviewerDisplayName;
    if (who) {
      var name = document.createElement("span");
      name.className = "nr-card__name";
      name.textContent = who;
      art.appendChild(name);
    }

    if (review.title) {
      var title = document.createElement("h3");
      title.className = "nr-card__title";
      title.textContent = review.title;
      art.appendChild(title);
    }

    var stars = document.createElement("span");
    stars.className = "nr-card__stars";
    stars.innerHTML = R.starsHTML(review.rating);
    art.appendChild(stars);

    if (review.body) {
      var body = document.createElement("p");
      body.className = "nr-card__body";
      body.textContent = review.body;
      art.appendChild(body);
    }

    return art;
  }

  function renderTrack(root, R, handle) {
    var track = root.querySelector("[data-nr-track]");
    if (!track) return;

    var min = parseFloat(root.getAttribute("data-nr-min-rating"));
    if (!isFinite(min)) min = R.MIN_HIGHLIGHT_RATING;
    var limit = parseInt(root.getAttribute("data-nr-limit"), 10);
    if (!isFinite(limit) || limit <= 0) limit = 6;

    var list = R.productReviews(handle, { minRating: min, limit: limit });

    while (track.firstChild) track.removeChild(track.firstChild);

    for (var i = 0; i < list.length; i++) {
      var card = buildCard(R, list[i], TINTS[i % TINTS.length]);
      if (card) track.appendChild(card);
    }

    /* Nudge assets/fitlibrary-nr.js so its arrow enable/disable state matches
       the track it can now measure. It recomputes on the track's own scroll
       event; a synthetic one is the smallest possible touch and needs no
       change to that controller. */
    try {
      track.dispatchEvent(new Event("scroll"));
    } catch (e) {
      try {
        var ev = document.createEvent("Event");
        ev.initEvent("scroll", false, false);
        track.dispatchEvent(ev);
      } catch (e2) {}
    }
  }

  /* ------------------------------------------------------------------ mount

     A section opts in by carrying data-nr-product with the canonical Shopify
     handle of its product. Association is by that handle alone — resolved
     against the catalogue the data snippet built from real Shopify products.
     Nothing here matches on family, colour, section name or template name, so
     a section can only ever show its own product's reviews. An unknown handle
     renders nothing rather than guessing. */

  function renderOne(root) {
    var R = source();
    if (!R || !root) return;

    var handle = root.getAttribute("data-nr-product");
    if (!handle) return;
    if (!R.product(handle)) return;

    renderProof(root, R, handle);
    renderTrack(root, R, handle);
  }

  function renderAll(scope) {
    var host = scope && scope.querySelectorAll ? scope : document;
    var found = host.querySelectorAll("[data-nr-product]");
    for (var i = 0; i < found.length; i++) renderOne(found[i]);
    if (host !== document && host.matches && host.matches("[data-nr-product]")) renderOne(host);
  }

  /* Render immediately. This script is deferred and is placed before
     assets/fitlibrary-nr.js, so the cards exist by the time the carousel
     controller measures them. */
  renderAll(document);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { renderAll(document); });
  }

  /* The one shared refresh path. FitLibraryReviews.refresh() drops its caches
     and fires this event; every surface in the theme re-asks its own two
     questions. Phase 2B swapping in a persistent provider redraws this page
     through exactly this listener, with no edit to any section. */
  document.addEventListener(EVENT, function () { renderAll(document); });

  document.addEventListener("shopify:section:load", function (e) { renderAll(e.target); });
})(window, document);
